import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBookOpen, faChevronLeft, faChevronDown, faCircle, faCircleCheck, faCircleDot, faMicrophone,
    faStop, faVolumeHigh, faHandPointer, faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import Grid from "../../Components/Grid/Grid";
import Processing from "../../Components/Processing/Processing";
import SchemeBar from "../../Components/SchemeBar/SchemeBar";
import { STATE_RULE, SRS_CELLS, buildConfig, scopeLabel, stateLabelOf, Severity } from "../../Functions/htScenarios";
import {
    GRID_COLS, GRID_ROWS, IMG, CellSwitches, Maneuvers, emptyManeuvers, buildSRSLayout,
    ownerCellOf, wantBarSepClosed, wantGenOn,
} from "../../Functions/srsScheme";
import { installSRSClicks, installSRSFit, SwitchInfo } from "../../Functions/srsInteract";
import { buildProgram, LearnProgram, LearnStep } from "../../Functions/htLearningProgram";
import ManeuverFile, { RowState } from "./ManeuverFile";
import "./HighTensionLearning.css";

// Session context of a learning run.
export interface HTLearningSession {
    teacherId: string;
    studentId: string;
    sessionId: string;
    initialState: string;   // state id (htScenarios.STATE_OPTIONS)
    finalState: string;     // state id
    cell: string;           // cell id / busbar / semistation / "*"
    sessionIdx?: number;    // sheet number printed on the maneuver file
    onFinish?: () => void;
}

// The trainee's progress through one dictated step.
type Phase = "idle" | "dispatch" | "repeat" | "execute" | "report" | "finished";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* The four phases of one dictated step, drawn as a compact icon rail: every
   phase is an icon pill and only the active one spells its name out, so the
   sequence stays readable without four full-width chips. */
const PHASE_RAIL: { key: Phase; icon: typeof faMicrophone; ro: string; en: string }[] = [
    { key: "dispatch", icon: faVolumeHigh, ro: "Dispoziție", en: "Order" },
    { key: "repeat", icon: faMicrophone, ro: "Confirmare", en: "Read-back" },
    { key: "execute", icon: faHandPointer, ro: "Execuție", en: "Execution" },
    { key: "report", icon: faMicrophone, ro: "Raportare", en: "Report" },
];

/**
 * "Invatare" (learning) module. Same SRS station and same interlocks as the
 * training module, but guided: for every maneuver of the exercise the internal
 * energy dispatcher (D.E.I.) dictates the order, the trainee repeats it back out
 * loud (checked with Whisper) and only then may operate that one switch on the
 * scheme. Everything is mirrored live into the maneuver sheet.
 */
const HighTensionLearning = ({ data, useStore, session } : { data: any, useStore: any, session: HTLearningSession }) => {
    const navigate = useNavigate();
    const [cookies] = useCookies();
    const en = cookies?.language === "en";

    const addSign = useStore((state: any) => state.addSign);
    const reset = useStore((state: any) => state.reset);
    const changeGrid = useStore((state: any) => state.changeGrid);
    const imageLocking = useStore((state: any) => state.imageLocking);
    const swapSignImage = useStore((state: any) => state.swapSignImage);

    const [painted, setPainted] = useState(false);
    const [program, setProgram] = useState<LearnProgram | null>(null);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState<Phase>("idle");
    const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
    const [fileOpen, setFileOpen] = useState(true);
    const [stepsOpen, setStepsOpen] = useState(false);
    const [accepted, setAccepted] = useState(false);
    // Mirrors for the autosave, which runs from callbacks that captured an older
    // render and would otherwise persist stale values.
    const acceptedRef = useRef(false);
    const rowStatesRef = useRef<Record<string, RowState>>({});
    // Resume runs exactly once per mount, and the dictation must wait for it.
    const resumedRef = useRef(false);
    const [resumeChecked, setResumeChecked] = useState(false);
    const [resumeMsg, setResumeMsg] = useState("");
    const [recording, setRecording] = useState(false);
    const [busyVoice, setBusyVoice] = useState(false);
    const [voiceMsg, setVoiceMsg] = useState<string>("");
    // What the transcription made of the last read-back, printed back to the
    // trainee: a rejected confirmation is otherwise indistinguishable from a
    // mis-heard one.
    const [heard, setHeard] = useState<string>("");
    const [report, setReport] = useState<{ score: number | null, findings: any, missing?: string[] } | null>(null);

    const cellMeta = useRef<Record<string, CellSwitches>>({});
    const maneuvers = useRef<Maneuvers>(emptyManeuvers());
    const logRef = useRef<any[]>([]);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    // The click handler and the recorder callbacks are installed once, so every
    // piece of state they read lives in a ref as well.
    const phaseRef = useRef<Phase>("idle");
    const stepRef = useRef<LearnStep | null>(null);
    const stepsRef = useRef<LearnStep[]>([]);
    const stepIdxRef = useRef<number>(0);
    // Index of the switch operation the trainee still owes inside the open step.
    const [opIdx, setOpIdx] = useState(0);
    const opIdxRef = useRef<number>(0);
    const setOp = (i: number) => { opIdxRef.current = i; setOpIdx(i); };
    useEffect(() => { phaseRef.current = phase; }, [phase]);

    const ownerCell = (x: number, y: number) => ownerCellOf(cellMeta.current, x, y);
    const setImg = (cell: [number, number] | undefined, img: any) => { if (cell) swapSignImage(cell[0], cell[1], 1, img); };

    const steps = program?.steps || [];
    const step: LearnStep | null = steps[stepIdx] || null;
    useEffect(() => { stepRef.current = step; }, [step]);
    useEffect(() => { acceptedRef.current = accepted; }, [accepted]);
    useEffect(() => { rowStatesRef.current = rowStates; }, [rowStates]);
    useEffect(() => { stepsRef.current = steps; }, [program]);   // eslint-disable-line react-hooks/exhaustive-deps
    // Keeps the index the callbacks read in sync with the rendered one.
    const goToStep = (idx: number) => { stepIdxRef.current = idx; setStepIdx(idx); };

    const now = () => {
        const d = new Date();
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    };

    // ---- persistence ------------------------------------------------------

    // What is still to be executed when the trainee stops early.
    const missingOf = (idx: number) => stepsRef.current.slice(idx).map((s) => `${s.number} — ${s.fileText}`);

    /**
     * Everything needed to put the trainee back where they stopped.
     *
     * The switch positions are deliberately NOT stored: they are derivable by
     * replaying the ops of the steps already signed off, and a stored copy could
     * disagree with the program if the exercise definition ever changes.
     */
    const progressSnapshot = (idx: number) => ({
        stepIdx: idx,
        accepted: acceptedRef.current,
        rowStates: rowStatesRef.current,
        savedAt: Date.now(),
    });

    const autosave = (opts: { finished?: boolean, idx?: number } = {}) => {
        if (!BACKEND) return Promise.resolve(null);
        const all = stepsRef.current;
        const idx = Math.min(opts.idx ?? stepIdxRef.current, all.length);
        return fetch(`${BACKEND}/api/session-htlearning/edit-session-student-htlearning`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                teacherId: session.teacherId,
                studentId: session.studentId,
                sessionId: session.sessionId,
                currLogs: logRef.current,
                required: all.length,
                completed: idx,
                missing: opts.finished ? missingOf(idx) : [],
                finalStateMatches: opts.finished ? idx >= all.length : null,
                progress: progressSnapshot(idx),
            }),
        }).then((r) => r.json()).catch(() => null);
    };

    const logEvent = (kind: "maneuver" | "warning", severity: Severity | null, category: string, msg: string, cell?: string | null) => {
        const type = kind === "maneuver" ? "correct" : severity === "critical" || severity === "major" ? "wrong" : "warning";
        logRef.current.push({ kind, severity, category, msg, cell: cell ?? null, type, ts: Date.now() });
        autosave();
    };

    const finishSession = async (from?: number) => {
        const idx = Math.min(from ?? stepIdxRef.current, stepsRef.current.length);
        const res = await autosave({ finished: true, idx });
        if (BACKEND) {
            await fetch(`${BACKEND}/api/session-htlearning/finish-session-student-htlearning`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ teacherId: session.teacherId, studentId: session.studentId, sessionId: session.sessionId }),
            }).catch(() => null);
        }
        setPhase("finished");
        setReport({ score: res?.score ?? null, findings: res?.findings ?? null, missing: missingOf(idx) });
    };

    // ---- dispatcher voice --------------------------------------------------

    // Speaks a line through the backend TTS. Resolves when playback ends (or
    // immediately if the audio cannot be produced — the text stays on screen).
    const speak = (text: string) => new Promise<void>((resolve) => {
        if (!BACKEND) return resolve();
        fetch(`${BACKEND}/api/session-learning/text-to-speech/${data.userData.userId}/${encodeURIComponent(text)}`, {
            headers: { "Content-Type": "application/json" },
        })
            .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("tts"))))
            .then((blob) => {
                const url = URL.createObjectURL(new Blob([blob], { type: "audio/wav" }));
                const audio = new Audio(url);
                audioRef.current = audio;
                audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
                audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                audio.play().catch(() => resolve());
            })
            .catch(() => resolve());
    });

    // Dictate the step at `idx` and hand the turn over to the trainee.
    const dictate = async (idx: number) => {
        const s = stepsRef.current[idx];
        if (!s) return;
        setPhase("dispatch");
        setVoiceMsg("");
        setHeard("");
        setOp(0);
        setRowStates((prev) => ({ ...prev, [s.id]: { ...(prev[s.id] || {}), dispatchedAt: prev[s.id]?.dispatchedAt || now() } }));
        await speak(s.dispatcher);
        setPhase("repeat");
    };

    // ---- resume -----------------------------------------------------------

    /**
     * Put the scheme where the executed steps left it.
     *
     * Replayed from the program rather than restored from stored switch
     * positions: the ops are the definition of what each step does, so replaying
     * them cannot drift from the exercise the way a saved copy of the drawing
     * could.
     */
    const replayOps = (upTo: number) => {
        stepsRef.current.slice(0, upTo).forEach((st) => {
            st.ops.forEach((op) => {
                const closed = op.action === "close" || op.action === "start";
                if (op.type === "breaker") setImg(op.cell, closed ? IMG.intrClosed : IMG.intrOpen);
                else if (op.type === "clp") setImg(op.cell, closed ? IMG.clpClosed : IMG.clpOpen);
                else if (op.type === "generator") setImg(op.cell, closed ? IMG.genOn : IMG.genOff);
                else setImg(op.cell, closed ? IMG.sepClosed : IMG.sepOpen);   // bar + line separators
            });
        });
    };

    // Runs once, as soon as the program exists: asks the server where this
    // trainee stopped and, if there is anything to resume, rebuilds that state
    // before the dictation effect below starts talking.
    useEffect(() => {
        if (!program || resumedRef.current) return;
        resumedRef.current = true;
        if (!BACKEND) { setResumeChecked(true); return; }

        fetch(`${BACKEND}/api/session-htlearning/join-session-student-htlearning/${session.teacherId}/${session.studentId}/${session.sessionId}`,
            { headers: { "Content-Type": "application/json" } })
            .then((r) => r.json())
            .then((res) => {
                const p = res?.progress;
                const total = stepsRef.current.length;
                if (!p || typeof p.stepIdx !== "number" || p.stepIdx <= 0 || p.stepIdx > total) return;

                // Logs come back too, so the score keeps counting from where it
                // was instead of restarting at zero.
                if (Array.isArray(res.logs)) logRef.current = res.logs;

                replayOps(p.stepIdx);
                setRowStates(p.rowStates || {});
                if (p.accepted) setAccepted(true);
                goToStep(p.stepIdx);
                setResumeMsg(en
                    ? `Resumed at step ${p.stepIdx + 1} of ${total}.`
                    : `Reluat de la pasul ${p.stepIdx + 1} din ${total}.`);
            })
            .catch(() => { /* offline: start from the top rather than block the run */ })
            .finally(() => setResumeChecked(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [program]);

    // Start dictating as soon as the maneuver sheet is accepted — from the
    // resumed step, not from the first one, and only once the resume check has
    // finished so the two do not race.
    useEffect(() => {
        if (!accepted || !program || phase !== "idle" || !resumeChecked) return;
        if (!steps.length) { setPhase("finished"); return; }
        const from = Math.min(stepIdxRef.current, steps.length - 1);
        if (stepIdxRef.current >= steps.length) { setPhase("finished"); return; }
        dictate(from);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accepted, program, resumeChecked]);

    // ---- read-back (Whisper) ----------------------------------------------

    const startRecording = async () => {
        if (recording || busyVoice || (phase !== "repeat" && phase !== "report") || !step) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    noiseSuppression: false,
                    echoCancellation: false,
                    autoGainControl: false,
                    channelCount: 1,
                },
            });
            const recorder = new MediaRecorder(stream, { audioBitsPerSecond: 128000 });
            const chunks: BlobPart[] = [];
            recorder.ondataavailable = (e) => chunks.push(e.data);
            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                // The recorder's own mime type, not a guess. Chrome produces
                // WebM/Opus and Safari MP4/AAC; labelling either "audio/mp3"
                // was a lie the server had to ignore, and the upload
                // allowlist on /api/transcribe checks this field.
                await verifyReadBack(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
            };
            recorderRef.current = recorder;
            setHeard("");
            setRecording(true);
            recorder.start();
        } catch (e) {
            setVoiceMsg(en ? "The microphone is not available." : "Microfonul nu este disponibil.");
        }
    };

    const stopRecording = () => {
        const rec = recorderRef.current;
        if (rec && rec.state !== "inactive") rec.stop();
        setRecording(false);
    };

    const verifyReadBack = async (blob: Blob) => {
        const s = stepRef.current;
        if (!s || !BACKEND) return;
        // Before the maneuver the trainee repeats the order back; after it, they
        // report the result ("S-a adus in stare deconectata ...").
        const reporting = phaseRef.current === "report";
        const expected = (reporting ? s.report : s.confirm) || s.confirm;
        setBusyVoice(true);
        try {
            const formData = new FormData();
            formData.append("audio", blob, "audio_data.mp3");
            const request = await fetch(`${BACKEND}/api/session-learning/verify-audio/${encodeURIComponent(expected)}`, { method: "POST", body: formData });
            const response = await request.json();
            const verdict = response?.correct;                 // "correct" | "wrong" | "repeat"
            // Kept on screen through the next phase: on a wrong read-back it is
            // the only clue why it was refused.
            setHeard(typeof response?.heard === "string" ? response.heard.trim() : "");
            if (verdict === "correct") {
                setVoiceMsg("");
                setRowStates((prev) => ({ ...prev, [s.id]: { ...(prev[s.id] || {}), confirmedAt: now() } }));
                logEvent("maneuver", null, reporting ? "report" : "readback", `Confirmare corectă: ${expected}`, s.cellId);
                if (reporting) {
                    // D.E.I. acknowledges the report, then the next order follows.
                    setBusyVoice(false);
                    if (s.ack) await speak(s.ack);
                    completeStep(s);
                    return;
                }
                if (s.kind === "order" && s.ops.length) {
                    setPhase("execute");
                    await speak(en ? "Correct, execute!" : "Corect, execută!");
                } else completeStep(s);
            } else if (verdict === "repeat") {
                // The trainee asked the dispatcher to say it again.
                setVoiceMsg(en ? "The dispatcher repeats the order." : "Dispecerul repetă dispoziția.");
                await dictate(stepIdxRef.current);
            } else {
                // The dispatcher says it out loud as well, the way a real one would.
                const again = en ? "I did not understand, please repeat!" : "Nu am înțeles, repetă te rog!";
                setVoiceMsg(again);
                logEvent("warning", "minor", "readback", `Confirmare greșită la pasul ${s.number}: se aștepta "${expected}".`, s.cellId);
                setBusyVoice(false);
                await speak(again);
                return;
            }
        } catch (e) {
            setVoiceMsg(en ? "The read-back could not be checked." : "Confirmarea nu a putut fi verificată.");
        }
        setBusyVoice(false);
    };

    // ---- step completion ---------------------------------------------------

    // Marks the step done, moves the pointer on and either dictates the next
    // order or wraps the session up. Called both from the read-back check (for
    // verification / announcement steps) and from the scheme click handler.
    const completeStep = (s: LearnStep) => {
        const all = stepsRef.current;
        setRowStates((prev) => ({ ...prev, [s.id]: { ...(prev[s.id] || {}), confirmedAt: prev[s.id]?.confirmedAt || now(), done: true } }));
        const next = all.findIndex((x) => x.id === s.id) + 1;
        goToStep(next);
        stepRef.current = all[next] || null;
        if (next >= all.length) finishSession(next);
        else dictate(next);
    };

    // ---- scheme ------------------------------------------------------------

    // Set one cell's switches to a state rule (used to init the start state).
    const setCellState = (id: string, rule: typeof STATE_RULE[keyof typeof STATE_RULE]) => {
        const m = cellMeta.current[id];
        if (!m) return;
        m.barSeps.forEach((bs) => setImg(bs.cell, wantBarSepClosed(id, bs.bar, rule) ? IMG.sepClosed : IMG.sepOpen));
        setImg(m.lineSep, rule.lineSep === "closed" ? IMG.sepClosed : IMG.sepOpen);
        setImg(m.breaker, rule.breaker === "closed" ? IMG.intrClosed : IMG.intrOpen);
        setImg(m.clp, rule.clp === "closed" ? IMG.clpClosed : IMG.clpOpen);
        if (m.generator) setImg(m.generator, wantGenOn(rule) ? IMG.genOn : IMG.genOff);
    };

    useEffect(() => {
        if (!painted) return;
        return installSRSFit(GRID_COLS, GRID_ROWS);
    }, [painted]);

    // Only the switch the dispatcher just asked for may be operated, and only
    // once the order has been read back correctly.
    const guard = (info: SwitchInfo): string | null => {
        const s = stepRef.current;
        const op = s?.ops[opIdxRef.current];
        if (phaseRef.current !== "execute" || !op) {
            return en
                ? "Listen to the order and read it back before operating anything."
                : "Ascultă dispoziția și confirm-o înainte de a executa manevra.";
        }
        const [ex, ey] = op.cell;
        if (info.x !== ex || info.y !== ey) {
            const owner = ownerCell(info.x, info.y);
            logEvent("warning", "major", "wrong-switch",
                `Manevră greșită: s-a acționat ${info.what}${owner ? ` de la ${owner}` : ""} în loc de ${s?.fileText}`, owner);
            return en
                ? "This is not the switch the dispatcher asked for."
                : "Nu acesta este aparatul cerut de dispecer. Execută dispoziția primită.";
        }
        return null;
    };

    const onManeuver = (info: SwitchInfo) => {
        const s = stepRef.current;
        logEvent("maneuver", null, "maneuver", `${info.act} ${info.what}`, ownerCell(info.x, info.y));
        if (!s) return;
        // The sheet documents every operation with two rows (the maneuver and the
        // position check that follows it); both are done once the switch moved.
        const i = opIdxRef.current;
        const stamp = now();
        setRowStates((prev) => ({
            ...prev,
            [`${s.id}.${i * 2 + 1}`]: { dispatchedAt: stamp, confirmedAt: stamp, done: true },
            [`${s.id}.${i * 2 + 2}`]: { dispatchedAt: stamp, confirmedAt: stamp, done: true },
        }));
        if (i + 1 < s.ops.length) { setOp(i + 1); return; }
        // Every switch of the cell has been operated: the trainee reports back.
        setPhase("report");
    };

    useEffect(() => installSRSClicks({
        useStore,
        swapSignImage,
        maneuvers: () => maneuvers.current,
        guard,
        onManeuver,
        onWarning: (severity, category, msg, x, y) => logEvent("warning", severity, category, msg, ownerCell(x, y)),
    }), []);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        reset();
        changeGrid();
        imageLocking();

        const layout = buildSRSLayout(addSign);
        cellMeta.current = layout.cellMeta;
        maneuvers.current = layout.maneuvers;

        // The whole station starts in normal operation; only the selected scope
        // starts in the exercise's initial state.
        const cfg = buildConfig(session.initialState, session.finalState, session.cell);
        SRS_CELLS.forEach((c) => setCellState(c.id, STATE_RULE[cfg.start[c.id]]));

        logRef.current = [];
        setProgram(buildProgram(session.initialState, session.finalState, session.cell, layout.cellMeta, layout.maneuvers.sepBarLine));

        document.body.classList.add("show-current");
        document.body.classList.add("ht-scheme");
        document.body.classList.add("htl-fit");
        setPainted(true);

        return () => {
            document.body.classList.remove("show-current");
            document.body.classList.remove("ht-scheme");
            document.body.classList.remove("htl-fit");
            document.body.style.removeProperty("--htl-scale");
            document.body.style.removeProperty("--htl-offset");
            audioRef.current?.pause();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const progress = useMemo(() => {
        const total = steps.length || 1;
        return Math.round((Math.min(stepIdx, steps.length) / total) * 100);
    }, [stepIdx, steps.length]);

    // The exercise as the trainee thinks of it: not a list of switch orders but
    // a chain of operative states ("functiune" -> "stare deconectata" -> ...).
    // Each leg is done once every cell that moves on it has been signed off.
    const legs = useMemo(() => {
        const byIdx = new Map<number, { idx: number, from: string, to: string, total: number, doneCount: number, current: boolean }>();
        steps.forEach((s, i) => {
            if (s.legIdx < 0) return;                       // closing announcement
            const leg = byIdx.get(s.legIdx) || {
                idx: s.legIdx, from: s.legFrom, to: s.legTo, total: 0, doneCount: 0, current: false,
            };
            leg.total += 1;
            if (rowStates[s.id]?.done || i < stepIdx) leg.doneCount += 1;
            if (i === stepIdx) leg.current = true;
            byIdx.set(s.legIdx, leg);
        });
        // Array.from, not a spread: the project targets ES5, where spreading a
        // Map iterator does not compile.
        return Array.from(byIdx.values())
            .sort((a, b) => a.idx - b.idx)
            .map((l) => ({ ...l, done: l.doneCount >= l.total }));
    }, [steps, rowStates, stepIdx]);

    // The A3 page has a fixed size in mm, so it cannot reflow around the floating
    // chrome. Scale it down into the band left free between the top status strip
    // and the bottom dictation panel (and re-centre it on that band) so the whole
    // scheme stays visible whatever the panel currently says.
    useEffect(() => {
        if (!painted) return;
        const GAP = 10;
        // How much of the dictation panel has to stay clear of the scheme. The
        // drawing keeps a wide empty margin below its lowest bay labels, so the
        // page box may hang over the rest of the (translucent) panel without
        // covering anything that matters — reserving less here is what lets the
        // scheme be drawn bigger.
        const PANEL_CLEAR = 0.5;
        // Lifts the scheme off the exact centre of the band. Raise to move it up.
        const RISE = 26;
        // Slack kept inside the band so the topmost bay labels never run into the
        // scheme bar. Lower it to shrink the scheme, raise it to fill more.
        const FILL = 0.92;
        const fit = () => {
            const page = document.querySelector<HTMLElement>(".page-container");
            if (!page) return;
            const panelH = document.querySelector<HTMLElement>(".htl-panel")?.offsetHeight || 0;
            const barH = document.querySelector<HTMLElement>(".scheme-bar")?.offsetHeight || 0;
            // The free band: under the scheme bar, down to the part of the panel
            // that must remain visible.
            const bandTop = barH + GAP;
            const bandBottom = window.innerHeight - (panelH * PANEL_CLEAR + GAP);
            const band = bandBottom - bandTop;
            // offsetWidth/Height are the unscaled border-box, so the ratio below is
            // stable across renders (the transform does not feed back into it).
            if (band <= 0 || !page.offsetHeight || !page.offsetWidth) return;
            const scale = Math.min(1, (band * FILL) / page.offsetHeight, (window.innerWidth - 2 * GAP) / page.offsetWidth);
            // Centred in that band and then nudged up, so the scheme sits closer to
            // the bar than to the panel.
            const centreY = bandTop + band / 2 - RISE;
            document.body.style.setProperty("--htl-scale", String(scale));
            document.body.style.setProperty("--htl-offset", `${centreY - window.innerHeight / 2}px`);
        };
        fit();
        const ro = new ResizeObserver(fit);
        document.querySelectorAll<HTMLElement>(".htl-status, .htl-panel, .page-container").forEach((el) => ro.observe(el));
        window.addEventListener("resize", fit);
        return () => { ro.disconnect(); window.removeEventListener("resize", fit); };
    }, [painted, program, report]);

    if (!painted || !program) return <Processing />;

    const scope = scopeLabel(session.cell);
    // Index of the phase the trainee is on, so the rail can mark the earlier ones done.
    const phaseAt = PHASE_RAIL.findIndex((p) => p.key === phase);

    return (
        <>
            <SchemeBar
                data={data}
                title={en ? "Learning" : "Învățare"}
                onBack={() => navigate(-1)}
                hideBack
            >
                {!report ? (
                    <div className="htl-status">
                        <button
                            type="button"
                            className={`htl-status-btn${stepsOpen ? " open" : ""}`}
                            onClick={() => setStepsOpen((o) => !o)}
                            aria-expanded={stepsOpen}
                            title={en ? "See every step" : "Vezi toți pașii"}
                        >
                            {step ? <span className="htl-step-no">{step.number}</span> : null}
                            <span className="htl-scope">{scope}</span>
                            <span className="htl-target">
                                {stateLabelOf(session.initialState)} → {stateLabelOf(session.finalState)}
                            </span>
                            {/* Steps signed off / the step being worked on: 0/1 while
                                the first order is in progress, 1/2 on the second, and
                                total/total once the run is complete. */}
                            <span className="htl-progress-count">
                                {Math.min(stepIdx, steps.length)} / {Math.min(stepIdx + 1, steps.length) || steps.length}
                            </span>
                            <FontAwesomeIcon icon={faChevronDown} className="htl-status-caret" />
                        </button>

                        <div className="htl-progress"><i style={{ width: `${progress}%` }} /></div>

                        {/* Every step of the exercise, with the one in hand and
                            the ones already signed off. */}
                        {stepsOpen ? (
                            <div className="htl-steps-menu">
                                <p className="htl-steps-head">
                                    {en ? "State changes" : "Trecerile de stare"}
                                    <span>{legs.filter((l) => l.done).length} / {legs.length}</span>
                                </p>
                                <ol>
                                    {legs.map((leg) => (
                                        <li key={leg.idx} className={leg.done ? "done" : leg.current ? "current" : "todo"}>
                                            <span className="htl-steps-mark">
                                                {leg.done
                                                    ? <FontAwesomeIcon icon={faCircleCheck} />
                                                    : leg.current
                                                        ? <FontAwesomeIcon icon={faCircleDot} />
                                                        : <FontAwesomeIcon icon={faCircle} />}
                                            </span>
                                            <span className="htl-steps-text">
                                                <b>{leg.from}</b>
                                                <i>→</i>
                                                <b>{leg.to}</b>
                                            </span>
                                            <span className="htl-steps-count">{leg.doneCount} / {leg.total}</span>
                                            <span className="htl-steps-state">
                                                {leg.done
                                                    ? (en ? "Done" : "Executat")
                                                    : leg.current
                                                        ? (en ? "In progress" : "În lucru")
                                                        : (en ? "Pending" : "Urmează")}
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </SchemeBar>

            <div className="ht-back-btn" onClick={() => navigate(-1)} title={en ? "Back" : "Înapoi"}>
                <FontAwesomeIcon icon={faChevronLeft} className="back-icon" />
            </div>

            <Grid
                printSchemes={null}
                removePage={() => {}}
                totalPages={1}
                category="0"
                isSimulation={true}
                debugSchemeNumber={-1}
                page={1}
                useStore={useStore}
                data={data}
                projectId={null}
                format="A3"
                gridColumns={GRID_COLS}
                gridRows={GRID_ROWS}
                projectName="SRS - Învățare"
                teacherVar={true}
            />

            {/* Re-open the maneuver sheet at any time. */}
            <button type="button" className="htl-file-btn" onClick={() => setFileOpen(true)} title={en ? "Maneuver sheet" : "Foaia de manevră"}>
                <FontAwesomeIcon icon={faBookOpen} />
                <span className="htl-btn-label">{en ? "Maneuver sheet" : "Foaia de manevră"}</span>
            </button>


            {/* Dictation panel: phase rail + the controls, on two rows. The order
                itself and the read-back sentence are only spoken — printing them
                would make the panel tall enough to hide part of the scheme. */}
            {!report ? (
                <div className="htl-panel">
                    <div className="htl-top">
                        {step ? (
                            <div className="htl-phases">
                                {PHASE_RAIL.map((p, i) => {
                                    const done = phaseAt > i;
                                    return (
                                        <span key={p.key} className="htl-phase-slot">
                                            {i ? <i className={`htl-phase-arrow${phaseAt >= i ? " done" : ""}`} /> : null}
                                            <span className={`htl-phase${phase === p.key ? " on" : ""}${done ? " done" : ""}`}>
                                                <FontAwesomeIcon icon={done ? faCircleCheck : p.icon} />
                                                <span className="htl-phase-label">{en ? p.en : p.ro}</span>
                                            </span>
                                        </span>
                                    );
                                })}
                            </div>
                        ) : null}

                        <button type="button" className="htl-finish" onClick={() => finishSession()}>
                            {en ? "Finish" : "Finalizează"}
                        </button>
                    </div>

                    {step ? (
                        /* One row: what to do (plus the transcription feedback under
                           it) on the left, the controls on the right — the panel
                           floats over the scheme, so height is the scarce axis. */
                        <div className="htl-panel-row">
                            <div className="htl-msg">
                                {phase === "repeat" ? (
                                    <p className="htl-hint">{en ? "Read the order back out loud." : "Repetă dispoziția cu voce tare."}</p>
                                ) : phase === "execute" ? (
                                    <p className="htl-hint execute">
                                        {en ? "Now operate the requested switch on the scheme." : "Execută manevra cerută pe schemă."}
                                        {step.ops.length > 1 ? ` (${opIdx + 1} / ${step.ops.length})` : ""}
                                    </p>
                                ) : phase === "report" ? (
                                    <p className="htl-hint">{en ? "Report the result back to the dispatcher." : "Raportează dispecerului rezultatul manevrei."}</p>
                                ) : (
                                    <p className="htl-hint">{en ? "The dispatcher is speaking…" : "Dispecerul transmite dispoziția…"}</p>
                                )}

                                {/* Warning and read-back share one line with an ellipsis —
                                    the full transcription is in the title attribute. */}
                                {resumeMsg ? <p className="htl-resume">{resumeMsg}</p> : null}

                                {voiceMsg || heard ? (
                                    <p className="htl-sub">
                                        {voiceMsg ? <span className="htl-warn">{voiceMsg}</span> : null}
                                        {heard ? <span className="htl-heard" title={heard}>„{heard}”</span> : null}
                                    </p>
                                ) : null}
                            </div>

                            <div className="htl-actions">
                                <button type="button"
                                    className={`htl-mic${recording ? " rec" : ""}${busyVoice ? " busy" : ""}${!recording && !busyVoice && (phase === "repeat" || phase === "report") ? " ready" : ""}`}
                                    disabled={(phase !== "repeat" && phase !== "report") || busyVoice}
                                    title={en ? "Hold a normal speaking distance and read the order back" : "Vorbește de la distanță normală și repetă dispoziția"}
                                    onClick={() => (recording ? stopRecording() : startRecording())}>
                                    <FontAwesomeIcon icon={recording ? faStop : faMicrophone} />
                                    {/* Hidden until hover (see .htl-btn-label), so the
                                        controls read as icons over the scheme. The two
                                        transient states keep their label regardless —
                                        "recording" and "checking" are things the trainee
                                        has to see without hunting for them. */}
                                    <span className="htl-btn-label">
                                        {busyVoice
                                            ? (en ? "Checking…" : "Se verifică…")
                                            : recording
                                                ? (en ? "Stop" : "Oprește")
                                                : (en ? "Speak" : "Vorbește")}
                                    </span>
                                </button>
                                <button type="button" className="htl-repeat" disabled={phase === "dispatch"}
                                    title={en ? "Repeat the order" : "Repetă dispoziția"}
                                    aria-label={en ? "Repeat the order" : "Repetă dispoziția"}
                                    onClick={() => dictate(stepIdx)}>
                                    <FontAwesomeIcon icon={faRotateRight} />
                                    <span className="htl-btn-label">{en ? "Repeat the order" : "Repetă dispoziția"}</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="htl-hint">{en ? "No maneuver is needed for this exercise." : "Exercițiul nu necesită manevre."}</p>
                    )}
                </div>
            ) : null}

            {fileOpen ? (
                <ManeuverFile
                    rows={program.rows}
                    theme={program.theme}
                    purpose={program.purpose}
                    initialNote={program.initialNote}
                    sessionIdx={session.sessionIdx ?? 1}
                    states={rowStates}
                    activeStepId={step ? (phase === "execute" ? `${step.id}.${opIdx * 2 + 1}` : step.id) : null}
                    accepted={accepted}
                    onAccept={() => { setAccepted(true); setFileOpen(false); }}
                    onClose={() => setFileOpen(false)}
                    en={en}
                />
            ) : null}

            {report ? (
                <div className="ht-modal-overlay">
                    <div className="ht-modal ht-report">
                        <h2>{en ? "Session result" : "Rezultatul sesiunii"}</h2>
                        <p className="ht-report-score">
                            {report.score === null ? (en ? "Not scored" : "Fără punctaj") : `${en ? "Score" : "Scor"}: ${report.score} / 100`}
                        </p>
                        {report.findings && (["critical", "major", "minor"] as const).map((sev) => {
                            const items = report.findings[sev] || [];
                            if (!items.length) return null;
                            const title = sev === "critical" ? (en ? "Critical errors" : "Erori grave")
                                : sev === "major" ? (en ? "Mistakes" : "Greșeli") : (en ? "Warnings" : "Avertismente");
                            return (
                                <div key={sev} className={`ht-report-group ht-${sev}`}>
                                    <h3>{title} ({items.length})</h3>
                                    <ul>{items.map((it: any, i: number) => <li key={i}>{it.msg}</li>)}</ul>
                                </div>
                            );
                        })}
                        {report.missing && report.missing.length > 0 ? (
                            <div className="ht-report-group ht-major">
                                <h3>{en ? "Maneuvers not executed" : "Manevre neexecutate"} ({report.missing.length})</h3>
                                <ul>{report.missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
                            </div>
                        ) : null}
                        <div className="ht-modal-buttons">
                            <button type="button" className="ok" onClick={() => session.onFinish?.()}>
                                {en ? "See the results" : "Vezi clasamentul"}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default HighTensionLearning;
